/**
 * ChatGPT Ads Measurement Pixel — fire-and-forget browser events.
 * Pixel is installed only on routebot hostnames (see public/index.html).
 *
 * User matching fields go on oaiq("init"), never on individual measure calls.
 * Email / external id / phone / name are SHA-256 hashed in the browser first.
 */

const USER_STORAGE_KEY = "rb_oaiq_user";

const ASCII_PUNCTUATION = /[\s!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/g;

export function normalizeEmail(email) {
  return String(email).trim().toLowerCase();
}

export function normalizePhone(phone) {
  let digits = String(phone).replace(/[\s().-]/g, "").replace(/^\+/, "").replace(/^0+/, "");
  if (digits.length < 8 || digits.length > 15 || !/^\d+$/.test(digits)) return null;
  return digits;
}

export function normalizeNamePart(value) {
  return String(value).toLowerCase().replace(ASCII_PUNCTUATION, "");
}

export function splitName(name) {
  const parts = String(name)
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return {};
  if (parts.length === 1) return { firstName: parts[0] };
  return { firstName: parts[0], lastName: parts.slice(1).join(" ") };
}

export async function sha256Hex(value) {
  if (!value || typeof window === "undefined" || !window.crypto?.subtle) return null;
  try {
    const buf = await window.crypto.subtle.digest("SHA-256", new TextEncoder().encode(value));
    return Array.from(new Uint8Array(buf))
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("");
  } catch {
    return null;
  }
}

function readStoredUser() {
  try {
    const raw = sessionStorage.getItem(USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeStoredUser(user) {
  try {
    sessionStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  } catch {
    // private mode etc.
  }
}

export function applyStoredOpenAIUser() {
  try {
    if (typeof window === "undefined" || typeof window.oaiq !== "function") return;
    const user = readStoredUser();
    if (!user || typeof user !== "object" || Object.keys(user).length === 0) return;
    window.oaiq("init", { user });
  } catch {
    // analytics asla UI'ı kırmamalı
  }
}

export async function identifyOpenAIUser({ email, externalId, phone, name } = {}) {
  try {
    if (typeof window === "undefined" || typeof window.oaiq !== "function") return;

    const user = { ...(readStoredUser() || {}) };

    if (email) {
      const hashed = await sha256Hex(normalizeEmail(email));
      if (hashed) user.email_sha256 = hashed;
    }
    if (externalId) {
      const hashed = await sha256Hex(String(externalId).trim());
      if (hashed) user.external_id_sha256 = hashed;
    }
    if (phone) {
      const normalized = normalizePhone(phone);
      if (normalized) {
        const hashed = await sha256Hex(normalized);
        if (hashed) user.phone_number_sha256 = hashed;
      }
    }
    if (name) {
      const { firstName, lastName } = splitName(name);
      if (firstName) {
        const hashed = await sha256Hex(normalizeNamePart(firstName));
        if (hashed) user.first_name_sha256 = hashed;
      }
      if (lastName) {
        const hashed = await sha256Hex(normalizeNamePart(lastName));
        if (hashed) user.last_name_sha256 = hashed;
      }
    }

    if (Object.keys(user).length === 0) return;
    writeStoredUser(user);
    window.oaiq("init", { user });
  } catch {
    // analytics asla UI'ı kırmamalı
  }
}

export function measureOpenAIEvent(eventName, data = {}, options) {
  try {
    if (typeof window === "undefined" || typeof window.oaiq !== "function" || !eventName) return;
    if (options) window.oaiq("measure", eventName, data, options);
    else window.oaiq("measure", eventName, data);
  } catch {
    // analytics asla UI'ı kırmamalı
  }
}

export async function measureOpenAIRegistrationCompleted({ email, externalId, name } = {}) {
  await identifyOpenAIUser({ email, externalId, name });
  measureOpenAIEvent("registration_completed", { type: "customer_action" });
}
